import { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { useClient } from '../../../../context/ClientContext';
import { VALIDATION_LIMITS } from '../../../../utils/miscellaneous';
import { convertFeetAndInchesToCentimeters, convertCentimetersToFeetAndInches, convertKilogramsToPounds, convertPoundsToKilograms } from '../../../../utils/conversions';
import CommonErrorMessage from '../../common/CommonErrorMessage';
import ClientButton from '../../common/Clientbutton';
import StepsHeading from '../../common/StepsHeading';

const calculateBMI = ( heightInCm, weightInKg ) => {
  const heightInMeters = heightInCm / 100;
  return weightInKg / ( heightInMeters * heightInMeters );
};

// Calculate valid weight ranges based on height
const calculateValidRanges = ( userData ) => {
  // Constants for BMI limits
  const MIN_BMI = 10;
  const MAX_BMI = 65;

  // Get current values in metric
  let heightInCm = userData.heightUnit === "cm"
    ? Number( userData.height.cm )
    : convertFeetAndInchesToCentimeters( Number( userData.height.ft ), Number( userData.height.in ) );

  // Calculate valid weight range for current height
  const heightInMeters = heightInCm / 100;
  let minWeight = MIN_BMI * ( heightInMeters * heightInMeters );
  let maxWeight = MAX_BMI * ( heightInMeters * heightInMeters );

  // Apply absolute limits in kg first
  minWeight = Math.max( minWeight, VALIDATION_LIMITS.WEIGHT.KG.MIN );
  maxWeight = Math.min( maxWeight, VALIDATION_LIMITS.WEIGHT.KG.MAX );

  return {
    weightRanges: {
      min: userData.weightUnit === "kg"
        ? Math.round( minWeight )
        : Math.round( convertKilogramsToPounds( minWeight ) ),
      max: userData.weightUnit === "kg"
        ? Math.round( maxWeight )
        : Math.round( convertKilogramsToPounds( maxWeight ) ),
      unit: userData.weightUnit
    }
  };
};

export default function Step3_HeightWeight( { userData, setUserData, onNext } ) {
  const { theme } = useClient();

  // Measurement styles
  const measurementLabelStyle = {
    '& .MuiInputLabel-root': {
      fontSize: "19px",
      fontWeight: "500",
      textAlign: "right",
      width: "100%",
      color: "#000000"
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: "#000000",
      borderColor: "#000000",
    },
    '& .MuiInputBase-root.Mui-focused': {
      '& .MuiInput-underline': {
        borderBottom: '1px solid #000000 !important',
      },
      '&::after': {
        borderBottom: '1px solid #000000 !important',
      },
    },
  };

  // Toggle button styles
  const toggleButtonStyles = {
    textTransform: 'none',
    fontSize: '14px',
    padding: '4px 12px',
    color: "#000",
    backgroundColor: 'rgba(26, 26, 26, 0.08)', // Light gray background
    '&.Mui-selected': {
      backgroundColor: 'rgba(26, 26, 26, 0.3)', // Gray background
      color: "#fff",
    },
    minWidth: '40px',
    border: 'none',
    borderRadius: 0, // Remove individual button border radius
    '&:first-of-type': {
      borderTopLeftRadius: '4px',
      borderBottomLeftRadius: '4px'
    },
    '&:last-child': {
      borderTopRightRadius: '4px',
      borderBottomRightRadius: '4px'
    }
  };

  const toggleButtonGroupStyle = {
    backgroundColor: '#F5F5F5', // Light gray background
    borderRadius: '4px',
    height: '28px'
  };

  // Initialize userData structure if not provided
  const initialUserData = {
    heightUnit: userData.heightUnit || 'in',
    weightUnit: userData.weightUnit || 'lbs',
    height: userData.height || { ft: '', in: '', cm: '' },
    weight: userData.weight || '',
    isFrontendHeightImperialUnits: userData.heightUnit === 'in',
    isFrontendWeightImperialUnits: userData.weightUnit === 'lbs'
  };

  // Initialize validation state
  const [ validation, setValidation ] = useState( {
    isHeightValidated: { isTrue: true, errorMsg: '' },
    isWeightValidated: { isTrue: true, errorMsg: '' },
    isBMIValidated: { isTrue: true, errorMsg: '' }
  } );

  // Validate the form
  const validateForm = () => {
    const validateWeight = ( weight, weightUnit ) => {
      const numWeight = Number( weight );
      const limits = weightUnit === "kg"
        ? VALIDATION_LIMITS.WEIGHT.KG
        : VALIDATION_LIMITS.WEIGHT.LBS;

      if ( !weight ) {
        return {
          isValid: false,
          errorMsg: "Please enter your weight."
        };
      }

      if ( numWeight < limits.MIN || numWeight > limits.MAX ) {
        return {
          isValid: false,
          errorMsg: `Weight must be between ${ limits.MIN }${ weightUnit } and ${ limits.MAX }${ weightUnit }.`
        };
      }

      return { isValid: true, errorMsg: "" };
    };

    const validateHeight = ( height, heightUnit ) => {
      if ( heightUnit === "cm" ) {
        const numHeight = Number( height.cm );
        const limits = VALIDATION_LIMITS.HEIGHT.CM;

        if ( !height.cm ) {
          return {
            isValid: false,
            errorMsg: "Please enter your height."
          };
        }

        if ( numHeight < limits.MIN || numHeight > limits.MAX ) {
          return {
            isValid: false,
            errorMsg: `Height must be between ${ limits.MIN }cm and ${ limits.MAX }cm.`
          };
        }
      } else {
        const ft = Number( height.ft );
        const inches = Number( height.in );
        const limits = VALIDATION_LIMITS.HEIGHT.FT_IN;

        if ( !height.ft ) {
          return {
            isValid: false,
            errorMsg: "Please enter your height in feet."
          };
        }

        if ( !height.in ) {
          return {
            isValid: false,
            errorMsg: "Please enter your height in inches."
          };
        }

        // Convert height to total inches for comparison
        const totalInches = ( ft * 12 ) + inches;
        const minTotalInches = ( limits.MIN.FT * 12 ) + limits.MIN.IN;
        const maxTotalInches = ( limits.MAX.FT * 12 ) + limits.MAX.IN;

        if ( totalInches < minTotalInches || totalInches > maxTotalInches ) {
          return {
            isValid: false,
            errorMsg: `Height must be between ${ limits.MIN.FT }'${ limits.MIN.IN }" and ${ limits.MAX.FT }'${ limits.MAX.IN }".`
          };
        }
      }

      return { isValid: true, errorMsg: "" };
    };

    let hasError = false;
    const updatedValidation = { ...validation };

    // Height validation
    const heightValidation = validateHeight( userData.height, userData.heightUnit );
    updatedValidation.isHeightValidated = {
      isTrue: heightValidation.isValid,
      errorMsg: heightValidation.errorMsg
    };

    if ( !heightValidation.isValid ) hasError = true;

    // Weight validation
    const weightValidation = validateWeight( userData.weight, userData.weightUnit );
    updatedValidation.isWeightValidated = {
      isTrue: weightValidation.isValid,
      errorMsg: weightValidation.errorMsg
    };

    if ( !weightValidation.isValid ) hasError = true;

    // BMI validation (only if height and weight are valid)
    if ( heightValidation.isValid && weightValidation.isValid ) {
      const heightInCm = userData.heightUnit === "cm"
        ? Number( userData.height.cm )
        : convertFeetAndInchesToCentimeters(
          Number( userData.height.ft ),
          Number( userData.height.in )
        );

      const weightInKg = userData.weightUnit === "kg"
        ? Number( userData.weight )
        : convertPoundsToKilograms( Number( userData.weight ) );

      const bmi = calculateBMI( heightInCm, weightInKg );

      if ( bmi < 10 || bmi > 65 ) {
        const { weightRanges } = calculateValidRanges( userData );
        let errorMessage = "Height and weight combination results in an invalid BMI.";

        if ( userData.heightUnit === "cm" ) {
          errorMessage += ` For your height of ${ heightInCm }cm, please adjust weight to between ${ weightRanges.min }${ weightRanges.unit } and ${ weightRanges.max }${ weightRanges.unit }.`;
        } else {
          const ft = userData.height.ft;
          const inches = userData.height.in;
          errorMessage += ` For your height of ${ ft }'${ inches }", please adjust weight to between ${ weightRanges.min }${ weightRanges.unit } and ${ weightRanges.max }${ weightRanges.unit }.`;
        }

        updatedValidation.isBMIValidated = { isTrue: false, errorMsg: errorMessage };
        hasError = true;
      } else {
        updatedValidation.isBMIValidated = { isTrue: true, errorMsg: "" };
      }
    }

    return { isValid: !hasError, validation: updatedValidation };
  };

  // Handle unit toggle
  const handleUnitChange = ( event, newUnit, type ) => {
    if ( !newUnit ) return;

    setUserData( prev => ( {
      ...prev,
      ...( type === "height" ? {
        heightUnit: newUnit,
        height: { ft: "", in: "", cm: "" },
        isFrontendHeightImperialUnits: newUnit === "in"
      } : {
        weightUnit: newUnit,
        weight: "",
        isFrontendWeightImperialUnits: newUnit === "lbs"
      } )
    } ) );

    // Reset validation state when unit changes
    setValidation( prevValidation => ( {
      ...prevValidation,
      isWeightValidated: { isTrue: true, errorMsg: "" },
      isHeightValidated: { isTrue: true, errorMsg: "" },
      isBMIValidated: { isTrue: true, errorMsg: "" }
    } ) );
  };

  // Validate and proceed to next step
  const handleNextStep = () => {
    // Run form validation
    const { isValid, validation: updatedValidation } = validateForm();

    // Update validation state with results
    setValidation( updatedValidation );

    // If validation passes, proceed to next step
    if ( isValid ) {
      onNext();
    }
  };

  return (
    <Stack spacing={ 1 } alignItems="center" sx={ { gap: "17px" } }>
      <StepsHeading title="What is your height and weight?" />

      <Box sx={ {
        display: "flex",
        flexDirection: "column",
        width: {
          xs: "270px",
          sm: "300px",
          md: "400px",
          lg: "380px"
        },
      } }>
        {/* Height Section */ }

        <Box marginBottom="30px">
          <Stack display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" marginBottom={ 2 }>
            <Typography sx={ { fontSize: "17px", fontWeight: "500" } }>
              Height
            </Typography>
            <ToggleButtonGroup
              value={ userData.heightUnit || initialUserData.heightUnit }
              exclusive
              onChange={ ( event, newUnit ) => handleUnitChange( event, newUnit, "height" ) }
              sx={ toggleButtonGroupStyle }
            >
              <ToggleButton value="in" sx={ toggleButtonStyles }>in</ToggleButton>
              <ToggleButton value="cm" sx={ toggleButtonStyles }>cm</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          { ( userData.heightUnit || initialUserData.heightUnit ) === "in" ? (
            <Box sx={ { display: "flex", gap: 2 } }>
              <Box sx={ { width: "100%" } }>
                <TextField
                  label="ft"
                  variant="standard"
                  type="number"
                  value={ userData.height?.ft || '' }
                  onChange={ ( e ) => setUserData( prev => ( {
                    ...prev,
                    height: { ...prev.height, ft: e.target.value }
                  } ) ) }
                  sx={ measurementLabelStyle }
                  fullWidth
                  InputProps={ { disableUnderline: false } }
                  InputLabelProps={ { shrink: false } }
                />
              </Box>

              <Box sx={ { width: "100%" } }>
                <TextField
                  label="in"
                  variant="standard"
                  type="number"
                  value={ userData.height?.in || '' }
                  onChange={ ( e ) => setUserData( prev => ( {
                    ...prev,
                    height: { ...prev.height, in: e.target.value }
                  } ) ) }
                  sx={ measurementLabelStyle }
                  fullWidth
                  InputProps={ { disableUnderline: false } }
                  InputLabelProps={ { shrink: false } }
                />
              </Box>
            </Box>
          ) : (
            <Box>
              <TextField
                label="cm"
                variant="standard"
                type="number"
                fullWidth
                value={ userData.height?.cm || '' }
                onChange={ ( e ) => setUserData( prev => ( {
                  ...prev,
                  height: { ...prev.height, cm: e.target.value }
                } ) ) }
                sx={ measurementLabelStyle }
                InputProps={ { disableUnderline: false } }
                InputLabelProps={ { shrink: false } }
              />
            </Box>
          ) }
        </Box>


        {/* Weight Section */ }

        <Box marginBottom="10px">
          <Stack display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" marginBottom={ 2 }>
            <Typography sx={ { fontSize: "17px", fontWeight: "500" } }>
              Weight
            </Typography>
            <ToggleButtonGroup
              value={ userData.weightUnit || initialUserData.weightUnit }
              exclusive
              onChange={ ( event, newUnit ) => handleUnitChange( event, newUnit, "weight" ) }
              sx={ toggleButtonGroupStyle }
            >
              <ToggleButton value="lbs" sx={ toggleButtonStyles }>lbs</ToggleButton>
              <ToggleButton value="kg" sx={ toggleButtonStyles }>kg</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Box>
            <TextField
              label={ ( userData.weightUnit || initialUserData.weightUnit ) === "lbs" ? "lbs" : "kg" }
              variant="standard"
              type="number"
              fullWidth
              value={ userData.weight || '' }
              onChange={ ( e ) => setUserData( prev => ( {
                ...prev,
                weight: e.target.value
              } ) ) }
              sx={ measurementLabelStyle }
              InputProps={ { disableUnderline: false } }
              InputLabelProps={ { shrink: false } }
            />
          </Box>
        </Box>

        <CommonErrorMessage
          text={
            !validation.isHeightValidated.isTrue && validation.isHeightValidated.errorMsg ||
            !validation.isWeightValidated.isTrue && validation.isWeightValidated.errorMsg ||
            !validation.isBMIValidated.isTrue && validation.isBMIValidated.errorMsg
          }
        />
      </Box>
        <ClientButton
          text="Next"
          onClick={ handleNextStep }
          backgroundColor={ theme.primaryColor }
          sx={ {
            maxWidth: "320px",
            width: "100%",
          } }
        />

    </Stack>
  );
}